import React, { FC, ReactElement, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { GridColumnCheckbox, GridColumnSwitch } from '../columns/GridColumn';
import { GridColumnCheckboxProps, GridColumnSwitchProps } from '../columns/types';
import { useGridContext } from '../context/GridContext';
import { GridFiller } from '../GridFiller';
import { GridCheckboxHeader } from '../header/GridCheckboxHeader';
import { GridHeaderLabel } from '../header/GridHeaderLabel';
import { GridSwitchHeader } from '../header/GridSwitchHeader';
import { cellCSS, isColumnElement } from '../utils';

import { GridHeaderStacked } from './GridHeaderStacked';

export const GridHeaders: FC = () => {
  const {
    headers,
    depth,
    cumulative_column_widths,
    cumulative_fixed_left_widths,
    cumulative_fixed_right_widths,
    header_height,
    filler_required,
    column_widths,
    theme,
  } = useGridContext();

  const headers_stacked = useMemo(() => {
    let idx = 0;
    return headers.map((column, index) => {
      const children = React.Children.toArray(column.props.children || []).filter(isColumnElement);
      const count = children.length;

      if (count) {
        idx += count;
        return (
          <GridHeaderStacked
            key={`headers-stacked-header-${index}`}
            column={column}
            index={idx - count}
          />
        );
      }

      const style = cellCSS({
        height: header_height,
        depth,
        fixed: column.props.fixed,
        index: idx,
        width: column_widths[idx],
        filler_required,
        cumulative_column_widths,
        cumulative_fixed_left_widths,
        cumulative_fixed_right_widths,
      });

      if (column.type === GridColumnSwitch) {
        return (
          <GridSwitchHeader
            key={`headers-header-switch-${idx}`}
            index={idx++}
            column={column as ReactElement<GridColumnSwitchProps>}
            style={style}
          />
        );
      }

      if (column.type === GridColumnCheckbox) {
        return (
          <GridCheckboxHeader
            key={`headers-header-checkbox-${idx}`}
            index={idx++}
            column={column as ReactElement<GridColumnCheckboxProps>}
            style={style}
          />
        );
      }

      return (
        <GridHeaderLabel
          key={`headers-header-${idx}`}
          index={idx++}
          column={column}
          style={style}
        />
      );
    });
  }, [
    headers,
    column_widths,
    header_height,
    depth,
    filler_required,
    cumulative_column_widths,
    cumulative_fixed_left_widths,
    cumulative_fixed_right_widths,
  ]);

  return (
    <DndProvider backend={HTML5Backend}>
      {headers_stacked}
      <GridFiller height={header_height} depth={depth} theme={theme.header} />
    </DndProvider>
  );
};
